import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, X } from "lucide-react";
import { ArrowLeft, Search, DropDownBold } from "../AdminIcons";
import Loader from "../common/Loader";
import FormSelect from "../common/FormSelect";
import {
  useGetAllCertificatesQuery,
  useGetSingleCertificateQuery,
  useRevokeCertificateMutation,
  useRestoreCertificateMutation,
} from "../../services/certificate/certificateApi";
import { getApiErrorMessage, showToast } from "../../utils/toast";

const STATUS_OPTIONS = [
  { value: "all", label: "All Status" },
  { value: "active", label: "Active" },
  { value: "revoked", label: "Revoked" },
];

const getStatusTone = (status) => {
  switch (String(status || "").toLowerCase()) {
    case "revoked":
      return "bg-[#FEE2E2] text-[#991B1B]";
    case "active":
      return "bg-[#DCFCE7] text-[#166534]";
    default:
      return "bg-[#F3F4F6] text-[#374151]";
  }
};

const formatDate = (isoDate) => {
  if (!isoDate) return "-";
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatDateTime = (isoDate) => {
  if (!isoDate) return "-";
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const formatPersonName = (person) => {
  if (!person) return "-";
  const fullName = `${person.firstName || ""} ${person.lastName || ""}`.trim();
  return fullName || person.email || "-";
};

const DetailRow = ({ label, value }) => (
  <div className="flex flex-col gap-1 py-2.5 sm:flex-row sm:items-center sm:justify-between">
    <span className="text-sm text-[#6B7280]">{label}</span>
    <span className="text-sm font-medium text-[#111827]">{value ?? "-"}</span>
  </div>
);

const CertificateDetailsModal = ({ certificateId, onClose }) => {
  const {
    data: certificateResponse,
    isLoading,
    isFetching,
    isError,
  } = useGetSingleCertificateQuery(certificateId, {
    skip: !certificateId,
  });

  if (!certificateId) {
    return null;
  }

  const certificate = certificateResponse?.data;
  const isRevoked =
    String(certificate?.status || "").toLowerCase() === "revoked";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#1F1B24]/70 px-4">
      <div className="relative max-h-[min(90vh,calc(100dvh-2rem))] w-full max-w-[520px] overflow-y-auto rounded-[19px] bg-white px-6 pb-8 pt-6 sm:px-8">
        <button
          type="button"
          aria-label="Close certificate details"
          className="absolute right-6 top-5 text-[#151515] sm:right-8"
          onClick={onClose}
        >
          <X size={26} strokeWidth={2.4} />
        </button>

        <h3 className="pr-8 text-lg font-bold text-[#111827] sm:text-xl">
          Certificate Details
        </h3>

        {isLoading || isFetching ? (
          <div className="flex items-center justify-center py-16">
            <Loader size="lg" className="text-[#923D8C]" />
          </div>
        ) : isError || !certificate ? (
          <p className="py-10 text-center text-sm text-[#B91C1C]">
            Failed to load certificate details.
          </p>
        ) : (
          <div className="mt-4 divide-y divide-[#F3F4F6]">
            <DetailRow
              label="Student"
              value={
                certificate.studentName || formatPersonName(certificate.student)
              }
            />
            <DetailRow label="Email" value={certificate.student?.email} />
            <DetailRow
              label="Course"
              value={certificate.courseTitle || certificate.course?.courseTitle}
            />
            <DetailRow
              label="Instructor"
              value={certificate.instructorName}
            />
            <DetailRow label="Grade" value={certificate.grade} />
            <DetailRow
              label="Completion Date"
              value={formatDate(certificate.completionDate)}
            />
            <DetailRow
              label="Issue Date"
              value={formatDate(certificate.issueDate)}
            />
            <DetailRow
              label="Status"
              value={
                <span
                  className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold capitalize ${getStatusTone(
                    certificate.status,
                  )}`}
                >
                  {certificate.status || "N/A"}
                </span>
              }
            />

            {isRevoked ? (
              <>
                <DetailRow
                  label="Revoked At"
                  value={formatDateTime(certificate.revokedAt)}
                />
                <DetailRow
                  label="Revoked By"
                  value={formatPersonName(certificate.revokedBy)}
                />
                <DetailRow
                  label="Revoke Reason"
                  value={certificate.revokeReason}
                />
              </>
            ) : null}

            {certificate.restoredAt ? (
              <>
                <DetailRow
                  label="Restored At"
                  value={formatDateTime(certificate.restoredAt)}
                />
                <DetailRow
                  label="Restored By"
                  value={formatPersonName(certificate.restoredBy)}
                />
              </>
            ) : null}
          </div>
        )}

      </div>
    </div>
  );
};

const CertificatesView = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revokeReason, setRevokeReason] = useState("");
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [viewCertificateId, setViewCertificateId] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, statusFilter]);

  const queryParams = useMemo(
    () => ({
      page: currentPage,
      limit: 10,
      search: debouncedSearch,
      status: statusFilter === "all" ? "" : statusFilter,
    }),
    [currentPage, debouncedSearch, statusFilter],
  );

  const {
    data: certificatesResponse,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetAllCertificatesQuery(queryParams);

  const [revokeCertificate, { isLoading: isRevoking }] =
    useRevokeCertificateMutation();
  const [restoreCertificate, { isLoading: isRestoring }] =
    useRestoreCertificateMutation();

  const certificates = Array.isArray(certificatesResponse?.data)
    ? certificatesResponse.data
    : [];
  const totalPages = certificatesResponse?.pagination?.totalPages || 1;
  const totalCount =
    certificatesResponse?.pagination?.total || certificates.length;
  const shouldShowRows = !isLoading && !isFetching && !isError;

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const closeRevokeModal = () => {
    setRevokeTarget(null);
    setRevokeReason("");
  };

  const closeRestoreModal = () => setRestoreTarget(null);

  const handleConfirmRevoke = async () => {
    if (!revokeTarget?._id) {
      closeRevokeModal();
      return;
    }

    try {
      const response = await revokeCertificate({
        id: revokeTarget._id,
        reason: revokeReason.trim(),
      }).unwrap();

      if (response?.success === false) {
        showToast({
          type: "error",
          message: response?.message || "Failed to revoke certificate.",
        });
        return;
      }

      showToast({
        type: "success",
        message: response?.message || "Certificate revoked successfully.",
      });
      closeRevokeModal();
      refetch();
    } catch (error) {
      showToast({
        type: "error",
        message: getApiErrorMessage(error, "Failed to revoke certificate."),
      });
    }
  };

  const handleConfirmRestore = async () => {
    if (!restoreTarget?._id) {
      closeRestoreModal();
      return;
    }

    try {
      const response = await restoreCertificate(restoreTarget._id).unwrap();

      if (response?.success === false) {
        showToast({
          type: "error",
          message: response?.message || "Failed to restore certificate.",
        });
        return;
      }

      showToast({
        type: "success",
        message: response?.message || "Certificate restored successfully.",
      });
      closeRestoreModal();
      refetch();
    } catch (error) {
      showToast({
        type: "error",
        message: getApiErrorMessage(error, "Failed to restore certificate."),
      });
    }
  };

  return (
    <section className="min-h-screen bg-[#D9D9D914] px-4 pt-4 sm:px-7">
      <div className="mx-auto max-w-[1515px]">
        <button
          type="button"
          aria-label="Go back"
          className="text-[#4A4A68]"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft />
        </button>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
          <h2 className="text-xl font-bold text-[#111827] sm:text-2xl">
            Certificates
          </h2>
        </div>

        <div className="mt-4 overflow-hidden rounded-2xl border border-[#BCBCBC45] bg-white">
          <div className="flex flex-wrap items-center gap-4 border-b border-[#E5E7EB] p-4 sm:gap-5">
            <div className="relative min-w-[240px] flex-1">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
                <Search color="#9CA3AF" className="size-[16px]" />
              </span>
              <input
                type="text"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search by course title..."
                className="h-[38px] w-full rounded-lg border border-[#D1D5DB] bg-white pl-9 pr-3 text-sm font-normal leading-none text-[#111827] outline-none placeholder:text-[#9CA3AF]"
              />
            </div>

            <FormSelect
              id="certificate-status-filter"
              options={STATUS_OPTIONS}
              value={statusFilter}
              emptyByDefault={false}
              className="min-w-[150px]"
              controlWrapperClassName="mt-0"
              selectClassName="h-[38px] w-full rounded-lg border border-[#D1D5DB] bg-white px-3 text-sm font-normal leading-none text-[#111827]"
              dropdownClassName="left-0 min-w-[150px] overflow-hidden rounded-lg border border-[#D1D5DB] bg-white"
              optionClassName="px-3 py-2 text-sm text-[#111827]"
              icon={DropDownBold}
              iconProps={{ color: "#000" }}
              onChange={setStatusFilter}
            />
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[960px] border-separate border-spacing-0 text-left font-['Roboto']">
              <thead className="bg-[#B9B9B912] text-sm font-bold uppercase text-[#000]">
                <tr>
                  <th className="px-4 py-4 sm:px-6">Student</th>
                  <th className="px-4 py-4 sm:px-6">Course</th>
                  <th className="px-4 py-4 sm:px-6">Instructor</th>
                  <th className="px-4 py-4 sm:px-6">Grade</th>
                  <th className="px-4 py-4 sm:px-6">Completion Date</th>
                  <th className="px-4 py-4 sm:px-6">Status</th>
                  <th className="px-4 py-4 sm:px-6">Actions</th>
                </tr>
              </thead>

              <tbody>
                {isLoading || isFetching ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center">
                      <div className="flex items-center justify-center">
                        <Loader size="lg" className="text-[#923D8C]" />
                      </div>
                    </td>
                  </tr>
                ) : null}

                {isError && !isLoading && !isFetching ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-10 text-center text-sm text-[#B91C1C]"
                    >
                      Failed to load certificates.
                    </td>
                  </tr>
                ) : null}

                {shouldShowRows && certificates.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-10 text-center text-sm text-[#6B7280]"
                    >
                      {debouncedSearch || statusFilter !== "all"
                        ? "No certificates match the selected filters."
                        : "No certificates found."}
                    </td>
                  </tr>
                ) : null}

                {shouldShowRows &&
                  certificates.map((certificate) => {
                    const isRevoked =
                      String(certificate?.status || "").toLowerCase() ===
                      "revoked";

                    return (
                      <tr
                        key={certificate._id}
                        className="border-b border-[#EFEFFD] text-sm text-[#000]"
                      >
                        <td className="px-4 py-5 sm:px-6">
                          {certificate.studentName ||
                            `${certificate.student?.firstName || ""} ${
                              certificate.student?.lastName || ""
                            }`.trim() ||
                            "N/A"}
                        </td>
                        <td className="px-4 py-5 sm:px-6">
                          {certificate.courseTitle ||
                            certificate.course?.courseTitle ||
                            "N/A"}
                        </td>
                        <td className="px-4 py-5 sm:px-6 capitalize">
                          {certificate.instructorName || "N/A"}
                        </td>
                        <td className="px-4 py-5 sm:px-6">
                          {certificate.grade || "N/A"}
                        </td>
                        <td className="px-4 py-5 sm:px-6 text-[#878A8E]">
                          {formatDate(certificate.completionDate)}
                        </td>
                        <td className="px-4 py-5 sm:px-6">
                          <span
                            className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold capitalize ${getStatusTone(
                              certificate.status,
                            )}`}
                          >
                            {certificate.status || "N/A"}
                          </span>
                        </td>
                        <td className="px-4 py-5 sm:px-6">
                          <div className="flex flex-wrap items-center gap-3">
                            <button
                              type="button"
                              onClick={() =>
                                setViewCertificateId(certificate._id)
                              }
                              className="inline-flex items-center gap-1 text-sm font-medium text-[#923D8C] hover:underline"
                            >
                              <Eye size={14} />
                              View
                            </button>

                            {isRevoked ? (
                              <button
                                type="button"
                                onClick={() => setRestoreTarget(certificate)}
                                className="text-sm font-medium text-[#007AFF] hover:underline"
                              >
                                Active
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => setRevokeTarget(certificate)}
                                className="text-sm font-medium text-[#DC2626] hover:underline"
                              >
                                Revoke
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>

          {totalCount > 10 ? (
            <div className="flex items-center justify-between px-4 py-4 sm:px-6">
              <button
                type="button"
                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                disabled={currentPage === 1 || isFetching}
                className="inline-flex items-center gap-2 rounded-full border border-[#D8D0DD] bg-white px-6 py-2 text-sm font-medium transition hover:bg-[#FAF7FC] disabled:cursor-not-allowed disabled:opacity-40"
              >
                Previous
              </button>

              <p className="text-sm font-medium text-[#1F2937]">
                Page {currentPage}/{totalPages}
              </p>

              <button
                type="button"
                onClick={() =>
                  setCurrentPage((page) => Math.min(totalPages, page + 1))
                }
                disabled={currentPage >= totalPages || isFetching}
                className="inline-flex items-center gap-2 rounded-full border border-[#D8D0DD] bg-white px-6 py-2 text-sm font-medium transition hover:bg-[#FAF7FC] disabled:cursor-not-allowed disabled:opacity-40"
              >
                Next
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {revokeTarget ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#1F1B24]/70 px-4">
          <div className="relative w-full max-w-[447px] rounded-[19px] bg-white px-8 pb-10 pt-6">
            <button
              type="button"
              aria-label="Close revoke confirmation"
              className="absolute right-8 top-5 text-[#151515]"
              onClick={closeRevokeModal}
            >
              <X size={30} strokeWidth={2.4} />
            </button>

            <p className="pt-10 text-center text-base text-black">
              Revoke certificate for{" "}
              <span className="font-semibold">
                {revokeTarget.studentName}
              </span>
              ?
            </p>

            <textarea
              value={revokeReason}
              onChange={(event) => setRevokeReason(event.target.value)}
              placeholder="Reason for revoking (optional)"
              className="mt-4 h-24 w-full resize-none rounded-lg border border-[#D1D5DB] px-3 py-2 text-sm text-[#111827] outline-none"
            />

            <div className="mt-5 flex items-center justify-center gap-10">
              <button
                type="button"
                onClick={closeRevokeModal}
                className="min-w-[99px] rounded-full bg-[#D9D9D9] px-6 py-1.5 text-[15px] tracking-[3%] text-black"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleConfirmRevoke}
                disabled={isRevoking}
                className="inline-flex min-w-[99px] items-center justify-center gap-2 rounded-full bg-[#DC2626] px-6 py-1.5 text-[15px] tracking-[3%] text-white disabled:opacity-70"
              >
                {isRevoking ? (
                  <>
                    <Loader size="sm" />
                    <span>Revoking...</span>
                  </>
                ) : (
                  "Revoke"
                )}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {restoreTarget ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#1F1B24]/70 px-4">
          <div className="relative w-full max-w-[447px] rounded-[19px] bg-white px-8 pb-10 pt-6">
            <button
              type="button"
              aria-label="Close restore confirmation"
              className="absolute right-8 top-5 text-[#151515]"
              onClick={closeRestoreModal}
            >
              <X size={30} strokeWidth={2.4} />
            </button>

            <p className="pt-10 text-center text-base text-black">
              Restore certificate for{" "}
              <span className="font-semibold">
                {restoreTarget.studentName}
              </span>
              ?
            </p>

            <div className="mt-5 flex items-center justify-center gap-10">
              <button
                type="button"
                onClick={closeRestoreModal}
                className="min-w-[99px] rounded-full bg-[#D9D9D9] px-6 py-1.5 text-[15px] tracking-[3%] text-black"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleConfirmRestore}
                disabled={isRestoring}
                className="inline-flex min-w-[99px] items-center justify-center gap-2 rounded-full bg-[#923D8C] px-6 py-1.5 text-[15px] tracking-[3%] text-white disabled:opacity-70"
              >
                {isRestoring ? (
                  <>
                    <Loader size="sm" />
                    <span>Restoring...</span>
                  </>
                ) : (
                  "Restore"
                )}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {viewCertificateId ? (
        <CertificateDetailsModal
          certificateId={viewCertificateId}
          onClose={() => setViewCertificateId(null)}
        />
      ) : null}
    </section>
  );
};

export default CertificatesView;
